export interface CreateShopifyAccountResult {
  success: boolean;
  token?: string;
  shopifyCustomerId?: string;
  errorKind?: "password_breach" | "login_failed" | "other";
  message?: string;
}

interface CreateShopifyAccountArgs {
  email: string;
  password: string;
  storeFrontUrl: string;
  storeFrontAPI: string;
}

async function mintAccessToken(
  email: string,
  password: string,
  storeFrontUrl: string,
  storeFrontAPI: string
): Promise<string | null> {
  const authQuery = `
    mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

  const authResponse = await fetch(storeFrontUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storeFrontAPI,
      Accept: "application/json",
    },
    body: JSON.stringify({ query: authQuery, variables: { input: { email, password } } }),
  });

  const authResult = await authResponse.json();
  const authErrors = authResult?.data?.customerAccessTokenCreate?.customerUserErrors;
  if (authErrors?.length) return null;

  const token = authResult?.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken;
  if (token) {
    // 24 hour expiry, HTTPS only — same cookie AccountLogin/the old AccountRegister already set.
    document.cookie = `mdi_customer_token=${token}; path=/; max-age=86400; SameSite=Strict; Secure`;
  }
  return token || null;
}

async function fetchShopifyCustomerId(
  token: string,
  storeFrontUrl: string,
  storeFrontAPI: string
): Promise<string | null> {
  const customerQuery = `
    query {
      customer(customerAccessToken: "${token}") {
        id
      }
    }
  `;

  const customerResponse = await fetch(storeFrontUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storeFrontAPI,
      Accept: "application/json",
    },
    body: JSON.stringify({ query: customerQuery }),
  });

  const customerResult = await customerResponse.json();
  const customerId = customerResult?.data?.customer?.id;
  if (!customerId) return null;
  return customerId.split("/").pop() || customerId;
}

/**
 * Creates a Shopify customer with just email + password, then mints an access token.
 * If the email is already taken (e.g. a retried submission after this same call previously
 * succeeded but a later step failed), falls back to logging in with the same credentials —
 * same defensive behaviour the old single-submission flow had.
 */
export async function createShopifyAccount({
  email,
  password,
  storeFrontUrl,
  storeFrontAPI,
}: CreateShopifyAccountArgs): Promise<CreateShopifyAccountResult> {
  const query = `
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer {
          id
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const response = await fetch(storeFrontUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storeFrontAPI,
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables: { input: { email, password } } }),
  });

  const result = await response.json();
  const errors = result?.data?.customerCreate?.userErrors;

  if (errors?.length) {
    const emailTakenError = errors.find(
      (err: any) =>
        (err.message?.toLowerCase().includes("taken") || err.message?.toLowerCase().includes("already")) &&
        err.field?.includes("email")
    );

    if (emailTakenError) {
      const token = await mintAccessToken(email, password, storeFrontUrl, storeFrontAPI);
      if (!token) {
        return { success: false, errorKind: "login_failed" };
      }
      const shopifyCustomerId = await fetchShopifyCustomerId(token, storeFrontUrl, storeFrontAPI);
      return {
        success: true,
        token,
        shopifyCustomerId: shopifyCustomerId || undefined,
      };
    }

    const isPasswordBreachError =
      errors[0]?.field?.includes("password") && errors[0]?.message?.toLowerCase().includes("different");
    if (isPasswordBreachError) {
      return { success: false, errorKind: "password_breach" };
    }

    return {
      success: false,
      errorKind: "other",
      message: errors[0]?.message || errors.map((e: any) => e.message).join(", "),
    };
  }

  const newCustomerId = result?.data?.customerCreate?.customer?.id;
  const shopifyCustomerId = newCustomerId ? newCustomerId.split("/").pop() || newCustomerId : undefined;

  const token = await mintAccessToken(email, password, storeFrontUrl, storeFrontAPI);
  if (!token) {
    // Account was created but login failed — treat as a soft success without a token; caller
    // can't proceed to resume-enabled state but the account exists for a future login attempt.
    return { success: false, errorKind: "login_failed" };
  }

  return { success: true, token, shopifyCustomerId };
}

export interface UpdateShopifyCustomerResult {
  success: boolean;
  message?: string;
}

interface UpdateShopifyCustomerArgs {
  customerAccessToken: string;
  storeFrontUrl: string;
  storeFrontAPI: string;
  firstName?: string;
  lastName?: string;
  phone?: string; // E.164, e.g. +15551234567
  email?: string;
}

/**
 * Updates an already-created Shopify customer — used once name/phone are known (account-details)
 * and when the "change email" inline edit needs to rename the already-created Shopify account,
 * not just what gets submitted to MDI. First `customerUpdate` usage in this codebase.
 */
export async function updateShopifyCustomer({
  customerAccessToken,
  storeFrontUrl,
  storeFrontAPI,
  firstName,
  lastName,
  phone,
  email,
}: UpdateShopifyCustomerArgs): Promise<UpdateShopifyCustomerResult> {
  const query = `
    mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
      customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
        customer {
          id
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

  const customer: Record<string, unknown> = {};
  if (firstName) customer.firstName = firstName;
  if (lastName) customer.lastName = lastName;
  if (phone) customer.phone = phone;
  if (email) customer.email = email;

  if (Object.keys(customer).length === 0) {
    return { success: true };
  }

  const response = await fetch(storeFrontUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storeFrontAPI,
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables: { customerAccessToken, customer } }),
  });

  const result = await response.json();
  const errors = result?.data?.customerUpdate?.customerUserErrors;

  if (errors?.length) {
    return { success: false, message: errors[0]?.message };
  }

  return { success: true };
}

interface SetShopifyCustomerStateArgs {
  customerAccessToken: string;
  storeFrontUrl: string;
  storeFrontAPI: string;
  state: string;
}

export async function setShopifyCustomerState({
  customerAccessToken,
  storeFrontUrl,
  storeFrontAPI,
  state,
}: SetShopifyCustomerStateArgs): Promise<{ success: boolean }> {
  const createQuery = `
    mutation customerAddressCreate($customerAccessToken: String!, $address: MailingAddressInput!) {
      customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
        customerAddress {
          id
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

  try {
    const createResponse = await fetch(storeFrontUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storeFrontAPI,
        Accept: "application/json",
      },
      body: JSON.stringify({
        query: createQuery,
        variables: { customerAccessToken, address: { country: "United States", province: state } },
      }),
    });

    const createResult = await createResponse.json();
    const addressId = createResult?.data?.customerAddressCreate?.customerAddress?.id;
    if (!addressId) return { success: false };

    const defaultQuery = `
      mutation customerDefaultAddressUpdate($customerAccessToken: String!, $addressId: ID!) {
        customerDefaultAddressUpdate(customerAccessToken: $customerAccessToken, addressId: $addressId) {
          customerUserErrors {
            field
            message
          }
        }
      }
    `;

    await fetch(storeFrontUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storeFrontAPI,
        Accept: "application/json",
      },
      body: JSON.stringify({ query: defaultQuery, variables: { customerAccessToken, addressId } }),
    });

    return { success: true };
  } catch {
    return { success: false };
  }
}

interface GetShopifyCustomerStateArgs {
  customerAccessToken: string;
  storeFrontUrl: string;
  storeFrontAPI: string;
}

export async function getShopifyCustomerState({
  customerAccessToken,
  storeFrontUrl,
  storeFrontAPI,
}: GetShopifyCustomerStateArgs): Promise<string | null> {
  const query = `
    query {
      customer(customerAccessToken: "${customerAccessToken}") {
        defaultAddress {
          province
        }
      }
    }
  `;

  try {
    const response = await fetch(storeFrontUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storeFrontAPI,
        Accept: "application/json",
      },
      body: JSON.stringify({ query }),
    });

    const result = await response.json();
    return result?.data?.customer?.defaultAddress?.province || null;
  } catch {
    return null;
  }
}
